// Plain async helpers for booking API calls.
//
// These are thin wrappers over apiClient for use in contexts where a React
// hook (useMutation/useQuery) is not appropriate — e.g. an imperative
// "click → generate link → show modal" flow on the booking detail page.
//
// Read variants are intentionally bare-bones — callers (BookingDetailPage,
// CancellationsPage) wrap them in their own state so the raw token is never
// kept in shared React Query cache.

import axios from 'axios';
import { apiClient } from './client';
import { endpoints } from './endpoints';
import type {
  CancellationLinkGenerated,
  CancellationLinkStatus,
} from '../types/api';

/**
 * Staff acknowledge that a driver's receipt completed a cancelled booking.
 * Clears it from the bell count and the review tab; the history stays.
 */
export async function acknowledgeCompletedAfterCancellation(encId: string): Promise<void> {
  await apiClient.post(endpoints.bookings.acknowledgeCompletedAfterCancellation(encId), {});
}

/**
 * Issue a fresh customer-self-cancellation link for a booking. Returns
 * the raw URL + token EXACTLY ONCE — the server only stores its SHA-256
 * hash, so subsequent reads cannot reproduce the URL. Per server-side
 * decision D2 (cancellationToken.ts), this also invalidates any prior
 * unused token for the same booking.
 */
export async function generateCancellationLink(
  encId: string,
): Promise<CancellationLinkGenerated> {
  const res = await apiClient.post<CancellationLinkGenerated>(
    endpoints.bookings.cancellationLink(encId),
  );
  return res.data;
}

/**
 * Report whether a cancellation link is currently active for a booking.
 * Returns null when no token has ever been issued OR every prior token
 * is expired/used. The returned `url_obscured` is a fingerprint only and
 * is NOT a usable URL — UIs that need to copy the link must POST to
 * issue a fresh one.
 */
export async function readCancellationLink(
  encId: string,
): Promise<CancellationLinkStatus | null> {
  try {
    const res = await apiClient.get<CancellationLinkStatus | { link: null }>(
      endpoints.bookings.cancellationLink(encId),
    );
    const data = res.data;
    if (data === null || (data as { link?: null }).link === null) return null;
    return data as CancellationLinkStatus;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
}
